import type { MouseEvent, RefObject } from 'react';
import Message from '@standardsdigital/ui-components/Message';
import Heading, { HeadingSize } from '@standardsdigital/ui-components/Heading';
import { useTranslation } from 'react-i18next';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import styles from './FormErrorSummary.module.css';

interface ErrorEntry {
  fieldPath: string;
  message: string;
}

interface FormErrorSummaryProps<T extends FieldValues> {
  errors: FieldErrors<T>;
  formRef?: RefObject<HTMLElement | null>;
  translationValues?: Record<string, string | number>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function collectErrorEntries(
  value: unknown,
  path: string,
  errors: ErrorEntry[],
): void {
  if (!isRecord(value)) {
    return;
  }

  if (typeof value.message === 'string' && value.message.length > 0) {
    errors.push({ fieldPath: path, message: value.message });
    return;
  }

  Object.entries(value).forEach(([key, nested]) => {
    if (key === 'type' || key === 'types' || key === 'ref') {
      return;
    }

    const nestedPath = path ? `${path}.${key}` : key;
    collectErrorEntries(nested, nestedPath, errors);
  });
}

function focusFieldByPath(
  fieldPath: string,
  container: HTMLElement | null | undefined,
): boolean {
  if (!container) {
    return false;
  }

  const selector = `[name="${fieldPath}"], #${fieldPath}`;
  const field = container.querySelector<HTMLElement>(selector);
  if (!field) {
    return false;
  }

  field.focus();
  return true;
}

function FormErrorSummary<T extends FieldValues>({
  errors,
  formRef,
  translationValues,
}: Readonly<FormErrorSummaryProps<T>>) {
  const { t } = useTranslation();

  const errorEntries: ErrorEntry[] = [];
  collectErrorEntries(errors, '', errorEntries);

  if (errorEntries.length === 0) {
    return null;
  }

  const focusField = (fieldPath: string) => {
    focusFieldByPath(fieldPath, formRef?.current);
  };

  const handleErrorClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
  };

  return (
    <Message variant="danger" className={styles.container}>
      <div>
        <Heading level={3} size={HeadingSize.Xxs} className={styles.title}>
          {t('form.errorSummaryTitle')}
        </Heading>
        <div>
          <ul className="sd-list-bulleted sd-link-block-wrapper">
            {errorEntries.map((errorEntry) => (
              <li key={`${errorEntry.fieldPath}-${errorEntry.message}`}>
                <a
                  href={`#${errorEntry.fieldPath}`}
                  onClick={(event) => {
                    handleErrorClick(event);
                    focusField(errorEntry.fieldPath);
                  }}
                >
                  {translationValues
                    ? t(errorEntry.message as never, translationValues)
                    : errorEntry.message}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Message>
  );
}

export default FormErrorSummary;
